import { Routes } from '@angular/router';
import { Login } from './features/auth/login/login';
import { authGuard, guestGuard, subscriptionGuard, superAdminGuard } from './core/guards/auth.guard';
import { pendingChangesGuard } from './core/guards/pending-changes-guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'sign-in',
    pathMatch: 'full',
  },

  {
    path: 'download',
    loadComponent: () => import('./features/download/download').then(m => m.Download),
    canActivate: [guestGuard],
  },

  {
    path: 'sign-in',
    component: Login,
    canActivate: [guestGuard],
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./features/auth/sign-up/sign-up').then(m => m.SignUp),
    canActivate: [guestGuard],
  },
  {
    path: 'account-deletion-request',
    loadComponent: () => import('./features/account-deletion-request/account-deletion-request').then(m => m.AccountDeletionRequestComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'referral/:code',
    loadComponent: () => import('./features/auth/referral-code-page/referral-code-page').then(m => m.ReferralCodePage),
    canActivate: [guestGuard],
  },
  {
    path: '',
    loadComponent: () =>
      import('./features/layout/layout/layout').then((m) => m.Layout),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        //canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: 'insights',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/insights/insights').then((m) => m.Insights),
      },
      {
        path: 'system-errors',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./features/system-errors/system-errors').then((m) => m.SystemErrors),
      },
      {
        path: 'post-pay-access',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/post-pay-access/post-pay-access').then(
            (m) => m.PostPayAccess,
          ),
      },
      {
        path: 'reports',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/reports/reports').then((m) => m.Reports),
      },
      {
        path: 'creator-venue-report',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/creator-venue-report/creator-venue-report').then((m) => m.CreatorVenueReport),
      },

      {
        path: 'disputes',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/disputes/disputes').then((m) => m.Disputes),
      },
      {
        path: 'google-analytics',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/super-admin-google-analytics/super-admin-google-analytics').then(
            (m) => m.SuperAdminGoogleAnalytics
          ),
      },
      {
        path: 'creator',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/creatorlist/creatorlist').then((m) => m.Creatorlist),
      },
      {
        path: 'subscription-monitoring',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/subscription-monitoring/subscription-monitoring').then(
            (m) => m.SubscriptionMonitoring,
          ),
      },
      {
        path: 'creator-tagging-console',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/invite-flow-superadmin/creator-tagging-console/creator-tagging-console').then(
            (m) => m.CreatorTaggingConsole,
          ),
      },
      {
        path: 'venue-tagging-console',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/invite-flow-superadmin/venue-tagging-console/venue-tagging-console').then(
            (m) => m.VenueTaggingConsole,
          ),
      },

      {
        path: 'subscriptions-admin',
        redirectTo: 'subscription-monitoring',
      },
      {
        path: 'edit-business-profile',
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/edit-business-profile/edit-business-profile').then(
            (m) => m.EditBusinessProfile,
          ),
      },
      {
        path: 'my-profile',
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./features/my-profile/my-profile').then(
            (m) => m.MyProfile,
          ),
      },
      {
        path: 'showcases',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/showcases/showcases').then((m) => m.Showcases),
      },
      {
        path: 'showcases/showcase-creator-profile',
        canActivate: [subscriptionGuard],
        loadChildren: () =>
          import('./features/showcases/showcase-creator-profile/showcase-creator-profile.routes').then(
            (m) => m.showcaseCreatorProfileRoute,
          ),
      },
      {
        path: 'showcases/showcase-preview',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/showcases/showcase-preview-dialog/showcase-preview-dialog').then(
            (m) => m.ShowcasePreviewDialog,
          ),
      },
      {
        path: 'showcases/matching/:showcaseId',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/invite-first/creator-matching/creator-matching').then(
            (m) => m.CreatorMatching,
          ),
      },
      {
        path: 'showcases/shortlist/:showcaseId',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/invite-first/venue-shortlist/venue-shortlist').then(
            (m) => m.VenueShortlist,
          ),
      },
      {
        path: 'showcases/invitations/:showcaseId',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./features/invite-first/manage-invitations/manage-invitations').then(
            (m) => m.ManageInvitations,
          ),
      },
      {
        path: 'public-profile',
        loadChildren: () =>
          import('./features/public-profile/public-profile.routes').then(
            (m) => m.PublicProfile,
          ),
      },

      {
        path: 'chat',
        canActivate: [subscriptionGuard],
        loadComponent: () => import('./features/chat/chat').then((m) => m.Chat),
      },
      {
        path: 'subscription',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/subscription/subscription').then(
                (m) => m.Subscription,
              ),
          },
          {
            path: 'payment-setup',
            loadComponent: () =>
              import('./features/subscription/payment-setup/payment-setup').then(
                (m) => m.PaymentSetup,
              ),
          },
          {
            path: 'payment-callback',
            loadComponent: () =>
              import('./features/subscription/payment-callback/payment-callback').then(
                (m) => m.PaymentCallback,
              ),
          },
        ],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/settings').then((m) => m.Settings),
      },
      {
        path: 'team',
        loadComponent: () =>
          import('./features/settings/team-members/team-members').then(
            (m) => m.TeamMembers,
          ),
      },
      {
        path: 'scan-pass/:showcaseId',
        loadComponent: () =>
          import('./features/scan-pass/scan-pass').then(
            (m) => m.ScanPassComponent
          ),
      },
      {
        path: 'scan-pass',
        loadComponent: () =>
          import('./features/scan-pass/scan-pass').then(
            (m) => m.ScanPassComponent
          ),
      },
      {
        path: 'verify-pass/:token',
        loadComponent: () =>
          import('./features/verify-pass/verify-pass').then(
            (m) => m.VerifyPassComponent
          ),
      },
      {
        path: 'showcase-requests',
        canActivate: [subscriptionGuard],
        loadComponent: () =>
          import('./shared/components/collaboration-card/showcase-requests/showcase-requests').then(
            (m) => m.ShowcaseRequests,
          ),
      },
    ],
  },
  {
    path: 'sign-up',
    canActivate: [guestGuard],
    children: [
      {
        path: 'timeline',
        loadComponent: () =>
          import('./features/auth/forms/timeline/timeline').then(
            (m) => m.Timeline,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/auth/forms/form1/form1').then((m) => m.Form1),
      },
      {
        path: 'terms-and-conditions',
        loadComponent: () =>
          import('./features/auth/forms/form2/form2').then((m) => m.Form2),
      },
      {
        path: 'business-basics',
        loadComponent: () =>
          import('./features/auth/forms/form3/form3').then((m) => m.Form3),
      },
      {
        path: 'socials-and-reservations',
        loadComponent: () =>
          import('./features/auth/forms/form4/form4').then((m) => m.Form4),
      },
      {
        path: 'profile-logo',
        loadComponent: () =>
          import('./features/auth/forms/form5/form5').then((m) => m.Form5),
      },
      {
        path: 'profile-images',
        loadComponent: () =>
          import('./features/auth/forms/form6/form6').then((m) => m.Form6),
      },
      {
        path: 'contact-details',
        loadComponent: () =>
          import('./features/auth/forms/form7/form7').then((m) => m.Form7),
      },
      {
        path: 'payment-options',
        loadComponent: () =>
          import('./features/auth/forms/payment-options/payment-options').then(
            (m) => m.PaymentOptions,
          ),
      },

      {
        path: '',
        redirectTo: 'profile',
        pathMatch: 'full',
      },
    ],
  },

  {
    path: 'unauthorized',
    redirectTo: 'sign-in',
  },
  {
    path: 'not-found',
    redirectTo: 'sign-in',
  },

  // Marketing embed — public, no auth guard (for iframe embedding)
  {
    path: 'marketing-embed',
    loadComponent: () =>
      import('./features/marketing-embed/marketing-embed').then((m) => m.MarketingEmbed),
  },

  // Public invitation claim — no auth required
  {
    path: 'invitation/claim/:token',
    loadComponent: () =>
      import('./features/public-invitation-claim/public-invitation-claim').then(
        (m) => m.PublicInvitationClaimComponent
      ),
  },
  {
    path: 'invitation/collaboration/:token',
    loadComponent: () =>
      import('./features/public-invitation-claim/collaboration-details/collaboration-details').then(
        (m) => m.CollaborationDetailsComponent
      ),
  },

  // OAuth callback pages — must be public (no auth guard)
  {
    path: 'tiktok-callback',
    loadComponent: () =>
      import('./features/auth/tiktok-callback/tiktok-callback').then((m) => m.TikTokCallback),
  },

  {
    path: '**',
    redirectTo: 'not-found',
  },
];
